// Frame derivations — pure view facts projected from frame factors.
//
// Split out of `app/frame.ts` (C1 hotspot decomposition). Each is a pure
// projection the shell (AppShell) only renders; none writes frame state.

import { exhaustSwitchCase } from "@bilda/utils";
import type {
    DockState,
    OpenSecondary,
    WorkspaceSurface,
} from "../../components/AppShell/AppShell.js";
import type { ActiveTab } from "../../components/Navigation/types.js";
import type { StudioSurface } from "./types.js";

// Where the chat sits on each view (#437): one explicit case per view, so a
// new view has to say where its chat goes before it compiles.
//   dock — the floating chat beside the view: the views that show what the
//          assistant works on, reads or cites (a material, its quality report,
//          a source in the Library or the Knowledge bank, the classic Studio
//          canvas, and the conversation tab, a transient the frame docks at
//          once);
//   page — the view holds its own chat (the Studio's pages: the Chat tab of
//          its right column, pen Q65);
//   none — no chat: the workspace-wide pages (Settings, All projects, the
//          Journal), which have no subject for the assistant to work on.
type DockChat = { readonly type: "dock" };
type PageChat = { readonly type: "page" };
type NoChat = { readonly type: "none" };
export type ChatPlace = DockChat | PageChat | NoChat;

const DOCK: DockChat = { type: "dock" };
const PAGE: PageChat = { type: "page" };
const NONE: NoChat = { type: "none" };

export const chatPlaceOf = (
    active: ActiveTab,
    studioSurface: StudioSurface,
): ChatPlace => {
    switch (active.type) {
        case "resource":
        case "quality":
        case "search":
        case "knowledge":
        case "conversation":
            return DOCK;
        case "studio":
            return studioSurface === "pages" ? PAGE : DOCK;
        case "settings":
        case "projects":
        case "home":
            return NONE;
        default:
            return exhaustSwitchCase(active);
    }
};

// The right dock as ONE derived value — the single fact the panel body, the
// ribbon, and the resize handle all project from (ui-robustness.md §3.1). This
// COLLAPSES the two conditionals that caused the stuck-handle bug: the body was
// gated on one fact while the handle read the raw `openSecondary`, so a view
// without the dock left the handle floating without its panel. Now all come
// from here:
//   - the view has no dock chat        → `gone`      (nothing renders)
//   - dock chat, no slot summoned      → `collapsed` (the minimised chat)
//   - dock chat + a summoned slot      → `open`      (panel + handle)
// Pure — the shell (AppShell) only renders it.
export const dockState = (
    chat: ChatPlace,
    openSecondary: OpenSecondary,
): DockState =>
    chat.type !== "dock"
        ? { type: "gone" }
        : openSecondary === null
          ? { type: "collapsed" }
          : { type: "open", slot: openSecondary };

// The centre's surface register, DERIVED from the focused tab + whether an
// active conversation is EMPTY — the ONE fact the ambient-vs-paper background
// swap reads (the workspace-landing sketch's single idea; the type + the CSS
// live in AppShell). An EMPTY conversation tab is the landing (the
// WorkspaceLanding hero composer, sharing the chrome's ambient ground); any
// other focused tab — including a non-empty conversation — is a work surface
// lifted to paper. `activeConversationEmpty` is the derived nascency the shell
// already computes from the session's transcript length
// (hero-is-initial-conversation-state §1.1); it is ignored for non-conversation
// tabs. Pure — a projection of frame factors, mirroring
// `dockState`; the host derives the active tab off the
// (focused-leaf) pane tree and the shell only renders it.
export const workspaceSurface = (
    active: ActiveTab,
    activeConversationEmpty: boolean,
): WorkspaceSurface =>
    active.type === "conversation" && activeConversationEmpty
        ? { type: "landing" }
        : { type: "paper" };

// How far the bottom-right corner (the Help tab, the minimised assistant)
// stands in from the window's right edge (pen Q68): 40 in from the page's
// edge; where a panel holds that edge, 24 left of it, so the corner never
// lies over its composer's Send (staging 2026-10-30). The open assistant is
// `dockWidth` wide; where the dock is gone, the page may keep its own chat
// panel (`chat` is `page`: the Studio's three columns). A CSS length.
export const cornerInset = (
    dock: DockState,
    dockWidth: number,
    chat: ChatPlace,
): string => {
    const besidePanel = (width: string) => `calc(var(--space-6) + ${width})`;
    switch (dock.type) {
        case "open":
            return besidePanel(`${dockWidth}px`);
        case "collapsed":
            return "var(--space-10)";
        case "gone":
            return chat.type === "page"
                ? besidePanel("var(--studio-right-width)")
                : "var(--space-10)";
        default:
            return exhaustSwitchCase(dock);
    }
};
